import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { toast } from 'react-toastify'
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons'

import { auctions, notifications } from '../../actions'

import { translate } from '../../helpers/stringHelpers'

import Loading from '../../components/loading'
import Alert from '../../components/alert'
import Button from '../../components/button'
import ConfigContext from '../../contexts/configContext'

// We will attempt connection every CONNECTION_RETRY_WAIT_MS, up to CONNECTION_ATTEMPTS times
const CONNECTION_ATTEMPTS = 5
const CONNECTION_RETRY_WAIT_MS = 2000

// one ping every 6s
const PING_FREQUENCY_MS = 6000
// treat ping as non-responsive if it takes more than 4.5s to respond
const PING_TIMEOUT_MS = 4500

// one hour of idle pings allowed
const ALLOWED_PINGS = 600

const ACTIONS = {
  PING: 'Ping',
  PONG: 'PONG',
  SUBSCRIBE: 'SubscribeToAuction'
}

const BATCH_FLUSH_TIMEOUT = 500
const batchTimeout = (ms) => new Promise(resolve => setTimeout(resolve, ms))

/** Stable id for react-toastify; do not use `toast.warn` return value — it can be undefined on duplicate/update. */
const RECONNECTION_TOAST_ID = 'reconnectionToast'

let saleStateChangeEventLock
let collatedSaleStateChangeSales = {}
let collatedSaleStateChangeSaleStatuses = {}

const HOCWrapper = (ChildComponent) => {
  class HOComponent extends Component {
    static contextType = ConfigContext

    constructor () {
      super()
      this.state = {
        hasFailed: false,
        isLoading: true,
        connectionAttemptCount: 0,
        pingCount: 0,
        hasTimedOut: false
      }
    }

    clearScheduledPing = () => {
      if (this.scheduledPing) {
        window.clearTimeout(this.scheduledPing)
      }
    }

    clearScheduledTermination = () => {
      if (this.scheduledTermination) {
        window.clearTimeout(this.scheduledTermination)
      }
    }

    connect = (connectionAttemptCount = this.state.connectionAttemptCount) => {
      this.setState({
        isLoading: true,
        connectionAttemptCount
      })

      // Create WebSocket connection.
      this.socket = new WebSocket(window.testSocketUrl || this.props.websocketBase)

      // Connection opened
      this.socket.addEventListener('open', this.handleSocketOpen)

      // Connection closed
      this.socket.addEventListener('close', this.handleSocketClose)

      // Listen for messages
      this.socket.addEventListener('message', this.handleSocketMessage)

      this.dismissAlert()
    }

    componentDidUpdate () {
      const reconnectionModal = document.querySelector('.modal-dialog')
      if (this.state.hasFailed && !reconnectionModal) {
        this.dismissAlert()
        this.props.showRefreshModal(translate('JspPublicCodes_JSP_NETWORK_RECONNECTION_ERROR'), true)
      }
    }

    componentDidMount () {
      this.connect()
      if (window.Cypress) {
        window.app = this
      }
    }

    dismissAlert = () => {
      if (this.state.connectingAlert) {
        toast.dismiss(RECONNECTION_TOAST_ID)
        this.setState({
          connectingAlert: null
        })
      }
    }

    forceClose = () => {
      if (this.socket) {
        // Connection opened
        this.socket.removeEventListener('open', this.handleSocketOpen)

        // Connection closed
        this.socket.removeEventListener('close', this.handleSocketClose)

        // Listen for messages
        this.socket.removeEventListener('message', this.handleSocketMessage)

        this.clearScheduledPing()
        this.clearScheduledTermination()

        this.socket.close()
      }
    }

    handleSocketOpen = () => {
      this.setState({
        isLoading: false,
        connectionAttemptCount: 0
      })

      console.info('Opened websocket connection')
      this.dismissAlert()
      this.schedulePing()
    }

    handleSocketClose = (event) => {
      // 1000 is normal close. Anything else is an error, and we might want to retry to open
      if (event.code > 1000) {
        console.warn('Websocket connection closed', event.code, event.reason, `attempt ${this.state.connectionAttemptCount + 1}`)
        this.handleLossOfConnection()
      } else {
        console.info('Closed websocket connection successfully', event.code, event.reason)
      }
      delete this.socket
    }

    handleSocketMessage = (event) => {
      const { action, data, timestamp } = JSON.parse(event.data)
      console.info('Received websocket message', event)
      const parsedEvent = {
        action,
        data,
        timestamp
      }

      const { locale } = this.context

      if (action === ACTIONS.PONG) {
        this.clearScheduledTermination()
      } else if (this.state.pingCount) {
        this.setState({
          pingCount: 0
        })
      }

      this.props.onWebsocketEvent({
        type: action,
        data,
        timestamp
      }, locale, this.props.userUuid)

      if (window.bidjs.callback) {
        window.bidjs.callback(parsedEvent)
      }
      this.schedulePing()
    }

    handleLossOfConnection = () => {
      this.clearScheduledPing()
      if (!this.state.hasTimedOut) {
        if (this.state.connectionAttemptCount < CONNECTION_ATTEMPTS) {
          // show loader; toast only after several failures so we do not alarm on brief blips
          console.log('Attempt number', this.state.connectionAttemptCount + 1, 'to connect to websocket')
          const showReconnectionToast = this.state.connectionAttemptCount >= 3
          if (showReconnectionToast) {
            this.reconnectionToast()
          }
          this.setState({
            connectingAlert: showReconnectionToast ? RECONNECTION_TOAST_ID : null,
            connectionAttemptCount: this.state.connectionAttemptCount + 1,
            isLoading: true
          })
          window.setTimeout(this.connect, CONNECTION_RETRY_WAIT_MS)
        } else {
          this.setState({
            hasFailed: true
          })
        }
      }
    }

    ping = () => {
      if (this.socket) {
        const payload = {
          action: ACTIONS.PING
        }

        console.info('Pinging', this.state.pingCount)
        console.time('Ping Response Time')
        try {
          if (this.socket.readyState === WebSocket.OPEN) {
            this.socket.send(JSON.stringify(payload))
            this.scheduleTermination()
            this.setState({
              pingCount: this.state.pingCount + 1
            })
          } else {
            console.time('Ping Error, socket not open, will re-attempt connection')
            this.setState({
              hasFailed: false,
              hasTimedOut: false
            })
            this.handleLossOfConnection()
          }
        } catch (error) {
          console.time(`Ping Error, will re-attempt connection: ${error.message}`)
          this.handleLossOfConnection()
        }
      }
    }

    handleReconnect = () => {
      this.setState({
        hasTimedOut: false,
        pingCount: 0,
        isLoading: true
      }, this.connect)
    }

    reconnectionToast = () => toast.warn(
      translate('JspPublicCodes_JSP_WEBSOCKET_RECONNECTION'), {
        autoClose: false,
        position: toast.POSITION.TOP_CENTER,
        toastId: RECONNECTION_TOAST_ID
      })

    schedulePing = () => {
      if (this.socket) {
        this.clearScheduledPing()

        if (this.state.pingCount < ALLOWED_PINGS) {
          this.scheduledPing = window.setTimeout(this.ping, PING_FREQUENCY_MS)
        } else {
          this.timeout()
        }
      }
    }

    scheduleTermination = () => {
      if (this.socket) {
        this.scheduledTermination = window.setTimeout(this.terminate, PING_TIMEOUT_MS)
      }
    }

    sendEvent = (payload) => {
      if (this.socket) {
        this.socket.send(JSON.stringify(payload))
      }
    }

    subscribeToAuction = (uuids) => {
      this.props.onWebsocketEvent({
        type: 'SUBSCRIBE_TO_AUCTION'
      })
      if (this.socket) {
        const payload = {
          action: ACTIONS.SUBSCRIBE,
          data: uuids,
          id: this.props.userUuid
        }
        try {
          if (this.socket.readyState === WebSocket.OPEN) {
            console.info('Subscribing to', uuids)
            this.socket.send(JSON.stringify(payload))
            this.setState({
              isLoading: false
            })
            this.props.onWebsocketEvent({
              type: 'AUCTION_SUBSCRIBED'
            })
          } else {
            console.warn('Socket not open, will re-attempt connection')
            this.setState({
              hasFailed: false,
              hasTimedOut: false
            })
            this.handleLossOfConnection()
          }
        } catch (error) {
          console.error(error)
          console.warn('Will re-attempt connection')
          this.handleLossOfConnection()
        }
      }
    }

    terminate = () => {
      this.forceClose()
      this.connect(this.state.connectionAttemptCount + 1)
    }

    timeout = () => {
      this.setState({
        hasTimedOut: true
      })
      this.forceClose()
    }

    render () {
      if (this.state.hasTimedOut) {
        return (
          <div className='text-center'>
            <Alert color='warning'>
              <p>{translate('JspPublicCodes_JSP_WEBSOCKET_CONNECTION_TIMEOUT')}</p>
              <Button className='websocket__cta' color='warning' onClick={this.handleReconnect}>{translate('JspPublicCodes_JSP_WEBSOCKET_RECONNECT')}</Button>
            </Alert>
          </div>
        )
      }

      if (this.state.isLoading) {
        return (
          <div className='page__loader'>
            <Loading />
          </div>
        )
      }

      return (
        <ChildComponent
          {...this.props}
          sendEvent={this.sendEvent}
          subscribeToAuction={this.subscribeToAuction}
          isInteractive={!this.state.isLoading}
        />
      )
    }

    componentWillUnmount () {
      this.clearScheduledPing()
      this.forceClose()
      if (this.state.connectingAlert) {
        toast.dismiss(RECONNECTION_TOAST_ID)
      }
    }
  }

  HOComponent.propTypes = {
    onWebsocketEvent: PropTypes.func.isRequired,
    userUuid: PropTypes.string,
    websocketBase: PropTypes.string.isRequired,
    showRefreshModal: PropTypes.func.isRequired
  }

  HOComponent.defaultProps = {}

  function mapStateToProps (state) {
    return {
      userUuid: state.users.user.uuid,
      websocketBase: state.config.input.config.websocketBase
    }
  }

  function mapDispatchToProps (dispatch) {
    return {
      onWebsocketEvent: async (event, locale, userUuid) => {
        // Due to these types of events often being dispatched in large numbers, we effectively "debounce" the events here
        // The events are collated over a time period specified in BATCH_FLUSH_TIMEOUT, and then sent as a single "dispatch"ed action
        if (event.type === 'SALE_COMPLETED' || event.type === 'SALE_STARTED') {
          if (!saleStateChangeEventLock) {
            collatedSaleStateChangeSaleStatuses = {}
            collatedSaleStateChangeSales = {}
          }
          const listingUuid = event.data.saleStatus.listingUuid
          if (event.data.sale) {
            collatedSaleStateChangeSales[listingUuid] = Object.assign({}, event.data.sale, {
              timestamp: event.timestamp
            })
          }
          collatedSaleStateChangeSaleStatuses[listingUuid] = Object.assign({}, event.data.saleStatus, {
            timestamp: event.timestamp
          })
          if (!saleStateChangeEventLock) {
            saleStateChangeEventLock = true
            await batchTimeout(BATCH_FLUSH_TIMEOUT)
            const eventToSend = Object.assign({}, event, {
              data: Object.assign({}, event.data, {
                sales: collatedSaleStateChangeSales,
                saleStatuses: collatedSaleStateChangeSaleStatuses
              })
            })
            saleStateChangeEventLock = null
            return dispatch(auctions.onWebsocketEvent(eventToSend, locale, userUuid))
          }
          return null
        } else {
          return dispatch(auctions.onWebsocketEvent(event, locale, userUuid))
        }
      },
      showRefreshModal: (message, needsRefreshing) => dispatch(notifications.addGlobalNotification('danger', message, faCircleExclamation, needsRefreshing))
    }
  }

  return connect(
    mapStateToProps,
    mapDispatchToProps
  )(HOComponent)
}

export default HOCWrapper
